import { useParams } from "react-router-dom";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { useProperty } from "queries/useProperty";
import CoverSection from "pages/BuildingPage/VirtualAudit/sections/CoverSection";
import DataInputsSection from "pages/BuildingPage/VirtualAudit/sections/DataInputsSection";
import BoilerplateSection from "pages/BuildingPage/VirtualAudit/sections/BoilerplateSection";
import styles from "pages/BuildingPage/VirtualAudit/VirtualAuditPage.module.scss";
import { usePropertyFilterset } from "./usePropertyFilterset";
import PropertyBaselineSection from "./sections/PropertyBaselineSection";
import PropertyDecarbonizationSection from "./sections/PropertyDecarbonizationSection";
import PropertyProjectsSection from "./sections/PropertyProjectsSection";

function PropertyVirtualAuditPage() {
    const { propertyUid } = useParams<{ propertyUid: string }>();
    const { data: property, isLoading: isLoadingProperty } = useProperty(propertyUid);
    const { data: filtersetData, isLoading: isLoadingFilterset } = usePropertyFilterset(propertyUid);

    const filtersetUid = filtersetData?.createReportFilterset as string | undefined;

    if (isLoadingProperty || isLoadingFilterset || !property || !filtersetUid || !propertyUid) {
        return <LoadingSpinner />;
    }

    const buildingModels = property.buildingModels as (PropertyBuildingModel & {
        reportedBuildingModelCarbonReductionPlanId?: string;
    })[];
    const firstBuilding = buildingModels[0];
    const buildingModelUids = buildingModels.map((bm) => bm.buildingModelUid);

    const handlePrint = () => {
        const prevTitle = document.title;
        const date = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
        document.title = `Audette Virtual Audit - ${property.propertyName} - ${date}`;
        window.print();
        window.addEventListener(
            "afterprint",
            () => {
                document.title = prevTitle;
            },
            { once: true }
        );
    };

    return (
        <div className={styles.virtualAuditRoot}>
            <div className={styles.exportButtonRow}>
                <button type="button" className={styles.exportButton} onClick={handlePrint}>
                    Export PDF
                </button>
            </div>

            <div className={styles.auditSection}>
                <CoverSection
                    propertyName={property.propertyName}
                    streetAddress={firstBuilding?.location?.streetAddress}
                    city={firstBuilding?.location?.city}
                    stateProvince={firstBuilding?.location?.stateProvince}
                    postalZipCode={firstBuilding?.location?.postalZipCode}
                    analysisDate={new Date().toISOString()}
                />
                <PropertyBaselineSection filtersetUid={filtersetUid} />
            </div>

            <div className={styles.auditSection}>
                <PropertyDecarbonizationSection filtersetUid={filtersetUid} />
            </div>

            <div className={styles.auditSection}>
                <PropertyProjectsSection buildingModelUids={buildingModelUids} />
            </div>

            <div className={styles.auditSection}>
                <DataInputsSection buildingModel={null} property={property} />
            </div>

            <div className={styles.auditSection}>
                <BoilerplateSection />
            </div>
        </div>
    );
}

export default PropertyVirtualAuditPage;
